import { Link } from "react-router-dom";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";

const t = copy.howPage;

const section = "scroll-mt-24 border-t border-ground-edge px-4 py-20 md:px-6 md:py-28";
const eyebrow = "text-[11px] uppercase tracking-[0.24em] text-chrome-dim";
const h2 = "mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl";
const lede = "mt-5 max-w-2xl text-sm leading-relaxed text-chrome-dim md:text-base";
const primaryCta =
  "bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-money px-6 text-xs font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep";
const secondaryCta =
  "bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md border border-gold/60 px-6 text-xs font-bold uppercase tracking-[0.14em] text-gold transition-colors hover:bg-gold/10";

function Head({ id, eyebrow: e, heading, body }: { id: string; eyebrow: string; heading: string; body?: string }) {
  return (
    <div className="max-w-3xl">
      <p className={eyebrow}>{e}</p>
      <h2 id={`${id}-heading`} className={h2}>
        {heading}
      </h2>
      {body && <p className={lede}>{body}</p>}
    </div>
  );
}

function Tile({ icon }: { icon: string }) {
  return (
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-gold/10 text-2xl text-gold">
      <Icon name={icon as IconName} />
    </span>
  );
}

function Checks({ items, tone = "money" }: { items: readonly string[]; tone?: "money" | "gold" }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-chrome md:text-base">
          <Icon name="check" className={`mt-1 text-base ${tone === "money" ? "text-money" : "text-gold"}`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-ground-edge pt-32 md:pt-40">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(43,217,107,0.12)_0%,transparent_55%)]"
      />
      <div className="relative z-10 mx-auto w-full max-w-[1240px] px-4 pb-16 md:px-6 md:pb-24">
        <p className={eyebrow}>{t.hero.eyebrow}</p>
        <h1 className="mt-5 max-w-4xl font-headline text-5xl uppercase leading-[0.92] tracking-[-0.02em] text-chrome sm:text-6xl md:text-7xl lg:text-8xl">
          {t.hero.heading[0]} <span className="mt-2 block text-gold">{t.hero.heading[1]}</span>
        </h1>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-chrome-dim md:text-lg">{t.hero.sub}</p>
        <nav aria-label={t.hero.jumpLabel} className="mt-10 flex flex-wrap gap-2">
          {t.jump.map((j) => (
            <a
              key={j.id}
              href={`#${j.id}`}
              className="bf-focusable rounded-full border border-ground-edge px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-chrome-dim transition-colors hover:border-money/60 hover:text-money"
            >
              {j.label}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}

export function Mission() {
  return (
    <section id="mission" aria-labelledby="mission-heading" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="mission" eyebrow={t.mission.eyebrow} heading={t.mission.heading} body={t.mission.body} />
        <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {t.mission.points.map((p) => (
            <li key={p.title} className="rounded-2xl border border-ground-edge bg-ground-raised p-6">
              <Tile icon={p.icon} />
              <h3 className="mt-5 font-headline text-xl uppercase tracking-[0.01em] text-chrome">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-chrome-dim">{p.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function ContestSteps() {
  return (
    <section id="contest" aria-labelledby="contest-heading" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="contest" eyebrow={t.contest.eyebrow} heading={t.contest.heading} />
        <ol className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-5 md:gap-4">
          {t.contest.steps.map((step, i) => (
            <li key={step.title} data-testid="contest-step" className="relative md:pr-4">
              <div className="flex items-center gap-3">
                <span className="bf-tabular flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-gold font-headline text-lg text-gold">
                  {i + 1}
                </span>
                {i < t.contest.steps.length - 1 && <span aria-hidden className="hidden h-px flex-1 bg-ground-edge md:block" />}
              </div>
              <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-money">{step.who}</p>
              <h3 className="mt-2 font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-chrome-dim">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// A mock of the voting screen: numbered tracks, no names.
function BlindTracks() {
  return (
    <div aria-hidden className="rounded-2xl border border-ground-edge bg-ground p-5 md:p-6">
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex items-center gap-4 border-b border-ground-edge py-4 last:border-b-0">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ground-raised text-lg text-chrome">
            <Icon name="play" />
          </span>
          <div className="flex-1">
            <p className="font-headline text-lg uppercase text-chrome">Track #{n}</p>
            <p className="mt-1 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-chrome-dim">
              <Icon name="eyeOff" /> Producer hidden
            </p>
          </div>
          <span className="flex gap-0.5 text-sm text-gold">
            {[0, 1, 2, 3, 4].map((s) => (
              <Icon key={s} name={s < 5 - n ? "star" : "starLine"} />
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Voting() {
  return (
    <section id="voting" aria-labelledby="voting-heading" className={`${section} bg-ground`}>
      <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <Head id="voting" eyebrow={t.voting.eyebrow} heading={t.voting.heading} body={t.voting.body} />
          <div className="mt-10">
            <Checks items={t.voting.rules} />
          </div>
        </div>
        <BlindTracks />
      </div>
    </section>
  );
}

export function Judging() {
  return (
    <section id="judging" aria-labelledby="judging-heading" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="judging" eyebrow={t.judging.eyebrow} heading={t.judging.heading} body={t.judging.body} />
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {t.judging.points.map((p) => (
            <li key={p.title} className="border-t-2 border-gold/60 pt-6">
              <h3 className="font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-chrome-dim">{p.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const splitTone: Record<string, { bar: string; text: string }> = {
  fee: { bar: "bg-chrome-dim", text: "text-chrome" },
  backing: { bar: "bg-money-deep", text: "text-money" },
  producer: { bar: "bg-gold-foil", text: "text-gold" },
  fans: { bar: "bg-money", text: "text-money" },
};

export function Picks() {
  const p = t.picks;
  return (
    <section id="picks" aria-labelledby="picks-heading" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="picks" eyebrow={p.eyebrow} heading={p.heading} body={p.body} />
        <figure className="mt-12 rounded-2xl border border-ground-edge bg-ground-raised p-6 md:p-8">
          <figcaption className="flex flex-wrap items-baseline justify-between gap-3">
            <span className={eyebrow}>{p.exampleLabel}</span>
            <span className="figure text-4xl text-chrome md:text-5xl">{p.examplePool}</span>
          </figcaption>
          <div aria-hidden className="mt-6 flex h-12 overflow-hidden rounded-md md:h-14">
            {p.split.map((s) => (
              <div
                key={s.key}
                style={{ flexGrow: s.share }}
                className={`flex items-center justify-center text-sm font-black text-ground ${splitTone[s.key].bar}`}
              >
                <span className="bf-tabular">{s.share}%</span>
              </div>
            ))}
          </div>
          <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {p.split.map((s) => (
              <li key={s.key} data-testid="split-part">
                <p className={`figure text-3xl ${splitTone[s.key].text}`}>{s.amount}</p>
                <p className={`mt-1 text-xs font-bold uppercase tracking-[0.14em] ${splitTone[s.key].text}`}>{s.label}</p>
                <p className="mt-1 text-sm text-chrome-dim">{s.note}</p>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs leading-relaxed text-chrome-dim">{p.exampleNote}</p>
        </figure>
        <div className="mt-12 max-w-3xl">
          <h3 className="font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{p.rulesHeading}</h3>
          <div className="mt-6">
            <Checks items={p.rules} />
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhatYouNeed() {
  return (
    <section id="need" aria-labelledby="need-heading" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="need" eyebrow={t.need.eyebrow} heading={t.need.heading} />
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {t.need.groups.map((g) => (
            <li key={g.title} className="rounded-2xl border border-ground-edge bg-ground p-6 md:p-8">
              <Tile icon={g.icon} />
              <h3 className="mb-5 mt-5 font-headline text-xl uppercase tracking-[0.01em] text-chrome">{g.title}</h3>
              <Checks items={g.items} tone="gold" />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function GettingPaid() {
  const p = t.paid;
  return (
    <section id="paid" aria-labelledby="paid-heading" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="paid" eyebrow={p.eyebrow} heading={p.heading} />
        <ol className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {p.steps.map((s, i) => (
            <li key={s.title} className="relative rounded-2xl border border-money/25 bg-money/5 p-6 md:p-8">
              <p className="bf-tabular font-headline text-5xl leading-none text-money/40">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-4 font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-chrome-dim">{s.body}</p>
            </li>
          ))}
        </ol>
        <h3 className="mt-16 font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{p.waysHeading}</h3>
        <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2">
          {p.ways.map((w) => (
            <div key={w.title}>
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-gold">{w.title}</p>
              <Checks items={w.items} />
            </div>
          ))}
        </div>
        <p className="mt-10 flex max-w-3xl items-start gap-3 rounded-xl border border-gold/25 bg-gold/5 p-5 text-sm leading-relaxed text-chrome">
          <Icon name="info" className="mt-0.5 text-lg text-gold" />
          <span>{p.note}</span>
        </p>
      </div>
    </section>
  );
}

// Who pays to move the money, in one plain line and four facts. No fine
// print: a producer should not have to hunt for whether a fee is coming.
export function Fees() {
  const f = t.fees;
  return (
    <section id="fees" aria-labelledby="fees-heading" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="fees" eyebrow={f.eyebrow} heading={f.heading} body={f.body} />
        <div className="mt-10 max-w-3xl">
          <Checks items={f.items} />
        </div>
      </div>
    </section>
  );
}

export function Websites() {
  const w = t.websites;
  return (
    <section id="websites" aria-labelledby="websites-heading" className={`${section} bg-ground-raised`}>
      <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <Head id="websites" eyebrow={w.eyebrow} heading={w.heading} body={w.body} />
          <div className="mt-10">
            <Checks items={w.points} />
          </div>
          <Link to="/websites-for-producers" className={`${secondaryCta} mt-10 inline-flex`}>
            {w.cta} →
          </Link>
        </div>
        <div className="rounded-2xl border border-ground-edge bg-ground p-6 md:p-8">
          <p className={eyebrow}>{w.plansLabel}</p>
          <ul className="mt-4">
            {copy.websites.pricing.tiers.map((tier) => (
              <li
                key={tier.name}
                data-testid="plan"
                className="flex items-baseline justify-between gap-4 border-b border-ground-edge py-5 last:border-b-0"
              >
                <div>
                  <p className="font-headline text-2xl uppercase text-chrome">{tier.name}</p>
                  <p className="mt-1 text-sm text-chrome-dim">{tier.outcome}</p>
                </div>
                <p className="figure whitespace-nowrap text-3xl text-gold">
                  {tier.price}
                  <span className="text-sm font-medium text-chrome-dim">{w.perMonth}</span>
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function Refunds() {
  const r = t.refunds;
  return (
    <section id="refunds" aria-labelledby="refunds-heading" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="refunds" eyebrow={r.eyebrow} heading={r.heading} />
        <div className="mt-12 overflow-x-auto rounded-2xl border border-ground-edge">
          <table className="w-full min-w-[560px] text-left">
            <caption className="sr-only">{r.caption}</caption>
            <thead className="bg-ground-raised">
              <tr>
                <th scope="col" className="w-1/3 px-5 py-4 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
                  {r.columns.when}
                </th>
                <th scope="col" className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
                  {r.columns.what}
                </th>
              </tr>
            </thead>
            <tbody>
              {r.rows.map((row) => (
                <tr key={row.when} className="border-t border-ground-edge align-top">
                  <th scope="row" className="px-5 py-5 text-sm font-bold text-chrome">
                    <span className="flex items-start gap-3">
                      <Icon name="refund" className="mt-0.5 text-lg text-gold" />
                      {row.when}
                    </span>
                  </th>
                  <td className="px-5 py-5 text-sm leading-relaxed text-chrome-dim">{row.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function Audience() {
  return (
    <section id="for" aria-labelledby="for-heading" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <Head id="for" eyebrow={t.audience.eyebrow} heading={t.audience.heading} />
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {t.audience.items.map((a) => (
            <li key={a.title} className="rounded-2xl border border-ground-edge bg-ground p-6 md:p-8">
              <Tile icon={a.icon} />
              <h3 className="mt-5 font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{a.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-chrome-dim md:text-base">{a.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function Faq() {
  return (
    <section id="faq" aria-labelledby="faq-heading" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-4xl">
        <Head id="faq" eyebrow={t.faq.eyebrow} heading={t.faq.heading} />
        <div className="mt-12 border-t border-ground-edge">
          {t.faq.items.map((item) => (
            <details key={item.q} className="group border-b border-ground-edge">
              <summary className="bf-focusable flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-base font-bold text-chrome md:text-lg [&::-webkit-details-marker]:hidden">
                {item.q}
                <Icon name="add" className="text-xl text-gold transition-transform group-open:rotate-45" />
              </summary>
              <p className="max-w-3xl pb-6 text-sm leading-relaxed text-chrome-dim md:text-base">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  const c = t.cta;
  return (
    <section className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-4xl">
        <h2 className="font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-6xl">
          {c.heading[0]} <br />
          <span className="text-gold">{c.heading[1]}</span>
        </h2>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Link to="/contests" className={primaryCta}>
            {c.enter}
          </Link>
          <Link to="/" className={secondaryCta}>
            {c.pick}
          </Link>
        </div>
        {/* Terms and privacy are Go pages: plain anchors. */}
        <p className="mt-10 text-sm text-chrome-dim">
          {c.legal}{" "}
          <a href="/terms-of-use" className="bf-focusable text-chrome underline underline-offset-4 hover:text-gold">
            {c.terms}
          </a>{" "}
          {c.and}{" "}
          <a href="/privacy" className="bf-focusable text-chrome underline underline-offset-4 hover:text-gold">
            {c.privacy}
          </a>
          .
        </p>
      </div>
    </section>
  );
}
